import * as React from "react";
import useReactRouter from "use-react-router";
// components
import { Link } from "react-router-dom";
import { FormattedNumber, FormattedMessage } from "react-intl";
import FromNow from "@vingle/wtf-sdk/components/fromNow";
import { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import ShavedText from "common/components/shavedText";
// helper
import NumberFormatter from "@vingle/wtf-sdk/components/numberFormatter";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { useTrackTicket } from "@vingle/wtf-sdk/helpers/trackTicket";
import translateURLToAppURL from "@vingle/wtf-sdk/helpers/translateURLToAppURL";
// styles
const styles = require("./questionItem.scss");

interface IProps {
  question: Vingle.QnA.IQuestion;
  onLikeClick: (questionId: string, currentLikeState: boolean) => void;
  referralUrl?: string;
}

const QuestionItem = React.memo((props: IProps) => {
  const { question, onLikeClick, referralUrl } = props;
  const { location } = useReactRouter();
  const handleLikeClick = React.useCallback(() => {
    onLikeClick(question.id, question.relation.like);
  }, [question, onLikeClick]);
  const { handleRead, impressionRef } = useTrackTicket({
    contentType: "question",
    contentId: question.id,
    referralUrl: referralUrl || translateURLToAppURL(location.pathname),
  });
  const questionShowLink = new VingleURL.QuestionShow({
    questionId: question.id,
  });
  return (
    <div className={styles.listItemContainer}>
      <div className={styles.leftWrapper}>
        <div className={styles.titleWrapper}>
          <Link
            key={`compactList_${question.id}`}
            onClick={handleRead}
            innerRef={impressionRef}
            to={questionShowLink.toLocation({
              modal: true,
              withBrowserSearch: true,
            })}
          >
            <div className={styles.questionIconWrapper}>
              <IconWrapper
                icon={PACKS.INTEREST_SHOW.QNA.QUESTION_MARK}
                className={styles.questionIcon}
              />
            </div>
            <ShavedText
              className={styles.title}
              shaveHeight={48}
              value={question.content}
              endFix={
                <span className={styles.endFix}>
                  {question.resource && question.resource.type === "image" ? (
                    <IconWrapper
                      className={styles.attachment}
                      icon={PACKS.INTEREST_SHOW.QNA.IMAGE_ATTACHED}
                    />
                  ) : null}
                  {question.answer_count > 0 ? (
                    <span>
                      [
                      {
                        <NumberFormatter
                          num={question.answer_count}
                          digits={1}
                        />
                      }
                      ]
                    </span>
                  ) : null}
                </span>
              }
            />
          </Link>
        </div>
        <div className={styles.infoWrapper}>
          <div className={styles.date}>
            <Link
              key={`compactList_${question.id}`}
              to={questionShowLink.toLocation({
                modal: true,
                withBrowserSearch: true,
              })}
            >
              <FromNow givenDate={question.updated_at * 1000} />
            </Link>
            <div className={styles.sep}>ㆍ</div>
          </div>
          <div className={styles.answers}>
            <Link
              to={questionShowLink.toLocation({
                modal: true,
                withBrowserSearch: true,
                state: {
                  answer: true,
                },
              })}
            >
              <FormattedMessage id="interest_show.question.add_answers" />
            </Link>
          </div>
        </div>
      </div>
      <div className={styles.rightWrapper}>
        <div className={styles.like} onClick={handleLikeClick}>
          {question.relation.like ? (
            <IconWrapper
              icon={PACKS.INTEREST_SHOW.QNA.LIKE_SELECT}
              className={styles.likeIcon}
            />
          ) : (
            <IconWrapper
              icon={PACKS.INTEREST_SHOW.QNA.LIKE_NORMAL}
              className={styles.likeIcon}
            />
          )}
          <span>
            {question.like_count > 0 ? (
              <FormattedNumber value={question.like_count} />
            ) : (
              <FormattedMessage id="like" />
            )}
          </span>
        </div>
      </div>
    </div>
  );
});

QuestionItem.displayName = "QuestionItem";

export default QuestionItem;
